import { useCallback, useEffect, useRef, useState } from "react";

/*
 * GiftToCash buddy - one cartoon character, many moods.
 *   <Buddy mood="wave" bubble="Hi!" />
 * moods: idle | wow | thumb | point | wave | celebrate | think | aha | sad | shy
 * Fire a pop-up from anywhere:
 *   window.dispatchEvent(new CustomEvent("g2c:buddy", { detail: { mood: "celebrate", text: "Done!" } }))
 */

const SKIN = "#B9774B", SKIN_D = "#9A5E37", HOODIE = "#10B981", HOODIE_D = "#059669", HAIR = "#1F1A17";
const SPRING = "cubic-bezier(.3,1.6,.5,1)";
const reduced = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const MOODS = {
    idle:      { l: "down", r: "down", mouth: "smile", eyes: "normal", brows: "normal" },
    wow:       { l: "down", r: "down", mouth: "open", eyes: "wide", brows: "up" },
    thumb:     { l: "down", r: "thumb", mouth: "grin", eyes: "normal", brows: "up", fx: "sparkle" },
    point:     { l: "down", r: "point", mouth: "smile", eyes: "normal", brows: "normal" },
    wave:      { l: "down", r: "wave", mouth: "grin", eyes: "normal", brows: "up" },
    celebrate: { l: "up", r: "up", mouth: "laugh", eyes: "happy", brows: "up", fx: "confetti", body: "jump" },
    think:     { l: "down", r: "chin", mouth: "hmm", eyes: "up", brows: "think" },
    aha:       { l: "down", r: "up", mouth: "grin", eyes: "wide", brows: "up", fx: "bulb" },
    sad:       { l: "shrug", r: "shrug", mouth: "frown", eyes: "normal", brows: "sad", fx: "sweat" },
    shy:       { l: "cover", r: "cover", mouth: "smallSmile", eyes: "normal", brows: "up" },
    hold:      { l: "hold", r: "hold", mouth: "smile", eyes: "normal", brows: "normal" },
    football:  { l: "down", r: "down", mouth: "smile", eyes: "normal", brows: "normal", ball: "juggle", legs: "kick" },
    goal:      { l: "up", r: "up", mouth: "laugh", eyes: "happy", brows: "up", fx: "confetti", body: "jump", ball: "fly" },
    holdHappy: { l: "hold", r: "hold", mouth: "grin", eyes: "normal", brows: "up" },
};

// Arm drawn for the RIGHT shoulder (146,168); the left one is the mirror image.
function Arm({ pose }) {
    const sleeve = (d) => <path d={d} stroke={HOODIE} strokeWidth="21" fill="none" strokeLinecap="round" />;
    switch (pose) {
        case "thumb":
            return (
                <g>
                    {sleeve("M144 170 Q182 198 174 152")}
                    <rect x="163" y="128" width="24" height="22" rx="8" fill={SKIN} />
                    <rect x="167" y="104" width="10" height="28" rx="5" fill={SKIN} />
                    <path d="M165 136 h14 M165 142 h14" stroke={SKIN_D} strokeWidth="1.6" strokeLinecap="round" />
                </g>
            );
        case "point":
            return (
                <g>
                    {sleeve("M144 172 Q170 184 192 172")}
                    <circle cx="198" cy="171" r="10.5" fill={SKIN} />
                    <rect x="202" y="166" width="20" height="8" rx="4" fill={SKIN} />
                </g>
            );
        case "up":
            return (
                <g>
                    {sleeve("M146 168 Q170 134 166 100")}
                    <circle cx="165" cy="90" r="11" fill={SKIN} />
                </g>
            );
        case "wave":
            return (
                <g className="g2c-wave">
                    {sleeve("M146 168 Q170 134 166 104")}
                    <ellipse cx="166" cy="92" rx="12" ry="13" fill={SKIN} />
                    {[0, 1, 2, 3].map((i) => <rect key={i} x={155 + i * 6} y={72 + (i === 0 || i === 3 ? 4 : 0)} width="5.5" height="14" rx="2.75" fill={SKIN} />)}
                    <rect x="176" y="90" width="12" height="6" rx="3" fill={SKIN} transform="rotate(-30 176 93)" />
                </g>
            );
        case "chin":
            return sleeve("M146 170 Q178 214 126 150");
        case "cover":
            return null; // drawn in front of the face (FrontArm)
        case "hold": // forearms forward, hands at chest height (holding something in front)
            return (
                <g>
                    {sleeve("M146 170 Q178 206 170 194")}
                    <circle cx="170" cy="190" r="11" fill={SKIN} stroke={SKIN_D} strokeWidth="1.5" />
                    <rect x="158" y="184" width="10" height="5" rx="2.5" fill={SKIN} stroke={SKIN_D} strokeWidth="1" />
                </g>
            );
        case "shrug":
            return (
                <g>
                    {sleeve("M146 170 Q170 205 186 186")}
                    <ellipse cx="192" cy="180" rx="12" ry="8" fill={SKIN} />
                </g>
            );
        default: // down
            return (
                <g>
                    <rect x="137" y="160" width="21" height="80" rx="10.5" fill={HOODIE} transform="rotate(-9 148 165)" />
                    <circle cx="156" cy="242" r="10.5" fill={SKIN} />
                </g>
            );
    }
}

// Parts that sit in front of the face (hand on chin, hands over eyes)
function FrontArm({ pose }) {
    if (pose === "chin") {
        return (
            <g>
                <circle cx="113" cy="141" r="11" fill={SKIN} stroke={SKIN_D} strokeWidth="2" />
                <rect x="104" y="124" width="8" height="18" rx="4" fill={SKIN} stroke={SKIN_D} strokeWidth="2" transform="rotate(-20 108 133)" />
            </g>
        );
    }
    if (pose === "cover") {
        return (
            <g>
                <path d="M146 170 Q170 140 130 104" stroke={HOODIE} strokeWidth="21" fill="none" strokeLinecap="round" />
                <ellipse cx="117" cy="92" rx="17" ry="14" fill={SKIN} stroke={SKIN_D} strokeWidth="2" />
                <path d="M106 86 h20 M106 93 h21 M107 100 h18" stroke={SKIN_D} strokeWidth="1.4" strokeLinecap="round" />
            </g>
        );
    }
    return null;
}

const mirror = "translate(200 0) scale(-1 1)";

export function Buddy({ mood = "idle", look = "right", size = 170, bubble = null, bubbleSide = "left", flip = false, className = "", style, pants = "#1E293B" }) {
    const m = MOODS[mood] || MOODS.idle;
    const excited = mood !== "idle" && mood !== "think" && mood !== "point";
    // pupils: look direction (in un-flipped drawing space)
    let dx = look === "left" ? -3 : look === "down" ? 1 : 3;
    let dy = look === "down" ? 3 : m.eyes === "up" ? -3 : 1;
    if (flip) dx = -dx;
    const eyeRy = m.eyes === "wide" ? 10 : 9;
    const positioned = /\b(absolute|fixed)\b/.test(className);
    return (
        <div className={`select-none ${positioned ? "" : "relative"} ${className}`} style={style} aria-hidden="true">
            {/* speech bubble */}
            <div className={`absolute top-0 ${bubbleSide === "right" ? "left-[60%] rounded-bl-sm" : "right-[60%] rounded-br-sm"} z-10 whitespace-nowrap bg-white border border-slate-100 shadow-floating rounded-2xl px-3 py-2 text-sm font-semibold text-slate-800 transition-all duration-300`}
                style={{ opacity: bubble ? 1 : 0, transform: `translateY(${bubble ? 0 : 8}px) scale(${bubble ? 1 : 0.9})`, pointerEvents: "none" }}>
                {bubble || " "}
            </div>
            <svg viewBox="0 0 200 340" width={size} height={size * 1.7} className="overflow-visible" style={{ transform: flip ? "scaleX(-1)" : undefined }}>
                <ellipse cx="100" cy="332" rx="62" ry="7" fill="rgba(15,23,42,.14)" className={m.body === "jump" ? "g2c-shadow" : ""} />
                <g className={m.body === "jump" ? "g2c-jump" : "g2c-breathe"}>
                    {/* legs + shoes */}
                    <rect x="70" y="236" width="26" height="84" rx="11" fill={pants} />
                    <rect x="60" y="312" width="40" height="16" rx="8" fill="#fff" stroke="#CBD5E1" />
                    <rect x="60" y="322" width="40" height="5" rx="2.5" fill={HOODIE} />
                    <g className={m.legs === "kick" ? "g2c-kick" : ""}>
                        <rect x="104" y="236" width="26" height="84" rx="11" fill={pants} />
                        <rect x="102" y="312" width="40" height="16" rx="8" fill="#fff" stroke="#CBD5E1" />
                        <rect x="102" y="322" width="40" height="5" rx="2.5" fill={HOODIE} />
                    </g>
                    {/* left arm (mirrored) */}
                    <g transform={mirror}><Arm pose={m.l} /></g>
                    {/* torso */}
                    <path d="M56 162 Q100 138 144 162 L151 252 Q100 266 49 252 Z" fill={HOODIE} />
                    <path d="M76 150 Q100 176 124 150" stroke={HOODIE_D} strokeWidth="7" fill="none" strokeLinecap="round" />
                    <line x1="100" y1="166" x2="100" y2="254" stroke={HOODIE_D} strokeWidth="2" />
                    <path d="M74 216 Q100 226 126 216 L121 242 L79 242 Z" fill={HOODIE_D} opacity=".45" />
                    <g transform={flip ? "translate(242 0) scale(-1 1)" : undefined}>
                        <circle cx="121" cy="186" r="8" fill="#fff" />
                        <text x="121" y="190" textAnchor="middle" fontSize="10" fontWeight="800" fill={HOODIE} fontFamily="Arial">G</text>
                    </g>
                    {/* right arm */}
                    <Arm pose={m.r} />
                    {/* neck */}
                    <rect x="89" y="126" width="22" height="24" rx="6" fill={SKIN_D} />
                    {/* head */}
                    <g style={{ transform: `rotate(${excited ? 6 : 3}deg) translateY(${mood === "wow" ? -4 : 0}px)`, transformOrigin: "100px 130px", transition: `transform .35s ${SPRING}` }}>
                        <circle cx="58" cy="96" r="9.5" fill={SKIN} />
                        <circle cx="142" cy="96" r="9.5" fill={SKIN} />
                        <ellipse cx="100" cy="92" rx="42" ry="46" fill={SKIN} />
                        <path d="M57 86 Q54 42 100 40 Q148 40 144 88 Q139 64 120 59 Q101 70 78 61 Q63 70 57 86 Z" fill={HAIR} />
                        <circle cx="72" cy="52" r="9" fill={HAIR} /><circle cx="90" cy="44" r="10" fill={HAIR} />
                        <circle cx="110" cy="43" r="10" fill={HAIR} /><circle cx="128" cy="50" r="9" fill={HAIR} />
                        {/* eyebrows */}
                        <g style={{ transform: `translateY(${m.brows === "up" ? -5 : 0}px)`, transition: `transform .25s ${SPRING}` }}>
                            {m.brows === "sad" ? (
                                <>
                                    <path d="M74 77 L92 71" stroke={HAIR} strokeWidth="4" strokeLinecap="round" />
                                    <path d="M108 71 L126 77" stroke={HAIR} strokeWidth="4" strokeLinecap="round" />
                                </>
                            ) : (
                                <>
                                    <path d="M74 76 Q83 70 92 75" stroke={HAIR} strokeWidth="4" fill="none" strokeLinecap="round" />
                                    <path d={m.brows === "think" ? "M108 70 Q117 64 126 70" : "M108 75 Q117 70 126 76"} stroke={HAIR} strokeWidth="4" fill="none" strokeLinecap="round" />
                                </>
                            )}
                        </g>
                        {/* eyes */}
                        {m.eyes === "happy" ? (
                            <g stroke={HAIR} strokeWidth="3.5" fill="none" strokeLinecap="round">
                                <path d="M75 93 Q83 84 91 93" /><path d="M109 93 Q117 84 125 93" />
                            </g>
                        ) : (
                            <g className="g2c-blink">
                                <ellipse cx="83" cy="90" rx="8" ry={eyeRy} fill="#fff" />
                                <ellipse cx="117" cy="90" rx="8" ry={eyeRy} fill="#fff" />
                                <g style={{ transform: `translate(${dx}px, ${dy}px)`, transition: "transform .3s ease" }}>
                                    <circle cx="84" cy="90" r="4.6" fill={HAIR} /><circle cx="118" cy="90" r="4.6" fill={HAIR} />
                                    <circle cx="85.5" cy="88.3" r="1.5" fill="#fff" /><circle cx="119.5" cy="88.3" r="1.5" fill="#fff" />
                                </g>
                            </g>
                        )}
                        <path d="M101 96 Q106 104 100 108" stroke={SKIN_D} strokeWidth="2.5" fill="none" strokeLinecap="round" />
                        <circle cx="73" cy="108" r="6" fill="#E07A5F" opacity={mood === "shy" ? 0.6 : 0.3} />
                        <circle cx="127" cy="108" r="6" fill="#E07A5F" opacity={mood === "shy" ? 0.6 : 0.3} />
                        {/* mouth */}
                        {m.mouth === "open" && (
                            <g><ellipse cx="101" cy="122" rx="10" ry="12" fill="#5B1F1A" /><ellipse cx="101" cy="129" rx="6" ry="4" fill="#E2725B" /></g>
                        )}
                        {(m.mouth === "grin" || m.mouth === "laugh") && (
                            <g>
                                <path d={m.mouth === "laugh" ? "M82 113 Q101 112 120 113 Q118 138 101 139 Q84 138 82 113 Z" : "M84 114 Q101 113 118 114 Q116 134 101 135 Q86 134 84 114 Z"} fill="#5B1F1A" />
                                <rect x="88" y="114" width="26" height="5" rx="2" fill="#fff" />
                                <ellipse cx="101" cy={m.mouth === "laugh" ? 133 : 130} rx="7" ry="3.5" fill="#E2725B" />
                            </g>
                        )}
                        {m.mouth === "smile" && <path d="M88 116 Q101 127 114 116" stroke="#5B2C1E" strokeWidth="3.5" fill="none" strokeLinecap="round" />}
                        {m.mouth === "smallSmile" && <path d="M93 118 Q101 124 109 118" stroke="#5B2C1E" strokeWidth="3.5" fill="none" strokeLinecap="round" />}
                        {m.mouth === "hmm" && <path d="M95 121 Q103 118 112 121" stroke="#5B2C1E" strokeWidth="3.5" fill="none" strokeLinecap="round" />}
                        {m.mouth === "frown" && <path d="M89 125 Q101 115 113 125" stroke="#5B2C1E" strokeWidth="3.5" fill="none" strokeLinecap="round" />}
                        {/* sweat drop */}
                        {m.fx === "sweat" && <path className="g2c-drop" d="M146 62 Q152 74 146 78 Q140 74 146 62 Z" fill="#7DD3FC" />}
                    </g>
                    {/* hands in front of the face */}
                    <FrontArm pose={m.r} />
                    {m.l === "cover" && <g transform={mirror}><FrontArm pose="cover" /></g>}
                    {/* effects */}
                    {m.fx === "sparkle" && (
                        <g fill="#FBBF24" className="g2c-pop">
                            <path d="M196 104 l3 7 7 3 -7 3 -3 7 -3 -7 -7 -3 7 -3z" />
                            <circle cx="152" cy="104" r="3" /><circle cx="198" cy="140" r="2.5" />
                        </g>
                    )}
                    {m.fx === "bulb" && (
                        <g className="g2c-pop">
                            <circle cx="150" cy="30" r="14" fill="#FDE047" stroke="#EAB308" strokeWidth="2" />
                            <rect x="144" y="42" width="12" height="8" rx="2" fill="#94A3B8" />
                            <path d="M150 6 v-6 M128 16 l-5 -4 M172 16 l5 -4 M124 34 h-7 M176 34 h7" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round" />
                        </g>
                    )}
                </g>
                {m.ball && (
                    <g className={m.ball === "fly" ? "g2c-ballfly" : "g2c-ball"}>
                        <g className="g2c-spin">
                          <g transform="translate(168 302) scale(1.4) translate(-166 -304)">
                            <circle cx="166" cy="304" r="13" fill="#fff" stroke="#334155" strokeWidth="1.5" />
                            <path d="M166 298 l5 3.6 -2 6 h-6 l-2 -6z" fill="#1E293B" />
                            <path d="M166 291.5 v6.5 M171 301.6 l6.5 -2 M169 307.6 l4 5 M163 307.6 l-4 5 M161 301.6 l-6.5 -2" stroke="#1E293B" strokeWidth="1.3" />
                          </g>
                        </g>
                    </g>
                )}
                {m.fx === "confetti" && (
                    <g>
                        {[["#F59E0B", 30, 0], ["#10B981", 60, .2], ["#6366F1", 140, .1], ["#EC4899", 170, .35], ["#0EA5E9", 100, .5], ["#F43F5E", 15, .6], ["#84CC16", 185, .15]].map(([c, x, d], i) => (
                            <rect key={i} x={x} y="-10" width="7" height="12" rx="2" fill={c} className="g2c-confetti" style={{ animationDelay: `${d}s` }} />
                        ))}
                    </g>
                )}
            </svg>
            <style>{`
                .g2c-blink{transform-box:fill-box;transform-origin:center;animation:g2cBlink 4.5s infinite}
                @keyframes g2cBlink{0%,94%,100%{transform:scaleY(1)}96%{transform:scaleY(.1)}}
                .g2c-breathe{animation:g2cBreathe 3.2s ease-in-out infinite}
                @keyframes g2cBreathe{0%,100%{transform:translateY(0)}50%{transform:translateY(-2px)}}
                .g2c-jump{animation:g2cJump .7s ease-in-out infinite}
                @keyframes g2cJump{0%,100%{transform:translateY(0)}45%{transform:translateY(-18px)}}
                .g2c-shadow{transform-box:fill-box;transform-origin:center;animation:g2cShadow .7s ease-in-out infinite}
                @keyframes g2cShadow{0%,100%{transform:scaleX(1)}45%{transform:scaleX(.7)}}
                .g2c-wave{transform-box:view-box;transform-origin:146px 168px;animation:g2cWave .9s ease-in-out infinite}
                @keyframes g2cWave{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(14deg)}}
                .g2c-pop{transform-box:fill-box;transform-origin:center;animation:g2cPop .45s ${SPRING} both}
                @keyframes g2cPop{from{transform:scale(0);opacity:0}to{transform:scale(1);opacity:1}}
                .g2c-drop{animation:g2cDrop 1.2s ease-in infinite}
                @keyframes g2cDrop{0%{transform:translateY(0);opacity:1}100%{transform:translateY(14px);opacity:0}}
                .g2c-confetti{transform-box:fill-box;transform-origin:center;animation:g2cConfetti 1.6s linear infinite}
                @keyframes g2cConfetti{0%{transform:translateY(0) rotate(0);opacity:1}100%{transform:translateY(200px) rotate(540deg);opacity:0}}
                .g2c-kick{transform-box:view-box;transform-origin:117px 240px;animation:g2cKick 1.1s ease-in-out infinite}
                @keyframes g2cKick{0%,30%,100%{transform:rotate(0)}12%{transform:rotate(-32deg)}}
                .g2c-ball{animation:g2cBall 1.1s infinite}
                @keyframes g2cBall{0%,12%{transform:translateY(0);animation-timing-function:cubic-bezier(.2,.7,.4,1)}56%{transform:translateY(-120px);animation-timing-function:cubic-bezier(.6,0,.8,.3)}100%{transform:translateY(0)}}
                .g2c-spin{transform-box:fill-box;transform-origin:center;animation:g2cSpin 1.1s linear infinite}
                @keyframes g2cSpin{to{transform:rotate(360deg)}}
                .g2c-ballfly{animation:g2cFly .9s cubic-bezier(.2,.6,.4,1) forwards}
                @keyframes g2cFly{0%{transform:translate(0,0) scale(1);opacity:1}100%{transform:translate(170px,-200px) scale(.4);opacity:0}}
                @media (prefers-reduced-motion: reduce){.g2c-kick,.g2c-ball,.g2c-spin,.g2c-ballfly{animation:none}}
                @media (prefers-reduced-motion: reduce){.g2c-blink,.g2c-breathe,.g2c-jump,.g2c-shadow,.g2c-wave,.g2c-drop,.g2c-confetti{animation:none}}
            `}</style>
        </div>
    );
}

// ── helpers ──────────────────────────────────────────────────────────────────
// Plays a short sequence of moods, then goes back to `base`.
export function useBuddy(base = "idle", baseBubble = null) {
    const [state, setState] = useState({ mood: base, bubble: baseBubble });
    const timers = useRef([]);
    const play = useCallback((steps) => {
        timers.current.forEach(clearTimeout);
        let t = 0;
        timers.current = steps.map((s) => {
            const at = t; t += s.ms || 1500;
            return setTimeout(() => setState({ mood: s.mood, bubble: s.bubble ?? null }), at);
        });
        timers.current.push(setTimeout(() => setState({ mood: base, bubble: baseBubble }), t));
    }, [base, baseBubble]);
    useEffect(() => () => timers.current.forEach(clearTimeout), []);
    return [state, play];
}

function useWindowEvent(name, fn) {
    const ref = useRef(fn); ref.current = fn;
    useEffect(() => {
        const h = (e) => ref.current(e);
        window.addEventListener(name, h);
        return () => window.removeEventListener(name, h);
    }, [name]);
}

// fires once when the element scrolls into view
function useSeenOnce(onSeen, threshold = 0.4) {
    const ref = useRef(null);
    const cb = useRef(onSeen); cb.current = onSeen;
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { cb.current(); io.disconnect(); } }, { threshold });
        io.observe(el);
        return () => io.disconnect();
    }, [threshold]);
    return ref;
}

// ── ready-made buddies for each place ────────────────────────────────────────

// Home hero: watches the calculator (event "g2c:card-picked" from ExchangeCalculator)
export function HeroBuddy({ className = "" }) {
    const [s, play] = useBuddy("idle");
    useWindowEvent("g2c:card-picked", (e) => {
        const { name = "", noOffer = false, currency = "" } = e.detail || {};
        if (noOffer) play([{ mood: "sad", bubble: `Hmm… no ${currency} offer for ${name} 😕`, ms: 2800 }]);
        else play([{ mood: "wow", bubble: "Wow! 😮", ms: 650 }, { mood: "thumb", bubble: `Nice pick${name ? `: ${name}` : ""}! 👍`, ms: 2300 }]);
    });
    return <Buddy mood={s.mood} bubble={s.bubble} className={className} />;
}

// How it works: waves hello when scrolled to, and again on hover
export function WaveBuddy({ className = "", text = "Hi! It's this easy 👋", size = 140 }) {
    const [s, play] = useBuddy("idle");
    const wave = () => play([{ mood: "wave", bubble: text, ms: 2600 }]);
    const ref = useSeenOnce(wave);
    return <div ref={ref} className={className} onMouseEnter={wave}><Buddy mood={s.mood} bubble={s.bubble} bubbleSide="right" size={size} /></div>;
}

// Live rates: points at the chart, reacts to USD/CAD switch (event "g2c:rates-currency")
export function RatesBuddy({ className = "" }) {
    const [s, play] = useBuddy("point");
    const ref = useSeenOnce(() => play([{ mood: "point", bubble: "Today's best rates 👉", ms: 2600 }]));
    useWindowEvent("g2c:rates-currency", (e) => {
        const c = e.detail?.currency;
        play([{ mood: "wow", bubble: c === "CAD" ? "Canadian rates! 🇨🇦" : "US rates! 🇺🇸", ms: 700 }, { mood: "thumb", bubble: c === "CAD" ? "Canadian rates! 🇨🇦" : "US rates! 🇺🇸", ms: 1500 }]);
    });
    return <div ref={ref} className={className}><Buddy mood={s.mood} bubble={s.bubble} bubbleSide="right" size={130} /></div>;
}

// FAQ: thinks; "aha" when a question is opened (any <details> inside `scope`)
export function ThinkBuddy({ className = "", scope = "#faq" }) {
    const [s, play] = useBuddy("think");
    useEffect(() => {
        const root = document.querySelector(scope);
        if (!root) return;
        const onToggle = (e) => { if (e.target?.open) play([{ mood: "aha", bubble: "Aha! 💡", ms: 1800 }]); };
        root.addEventListener("toggle", onToggle, true);
        return () => root.removeEventListener("toggle", onToggle, true);
    }, [scope, play]);
    return <div className={className}><Buddy mood={s.mood} bubble={s.bubble ?? (s.mood === "think" ? "Got questions? 🤔" : null)} bubbleSide="right" size={140} /></div>;
}

// CTA banner: celebrates when seen and when hovered
export function CelebrateBuddy({ className = "", text = "Let's go! 🎉", size = 170 }) {
    const [s, play] = useBuddy("thumb");
    const party = () => play([{ mood: "celebrate", bubble: text, ms: 2600 }]);
    const ref = useSeenOnce(party);
    return <div ref={ref} className={className} onMouseEnter={party}><Buddy mood={s.mood} bubble={s.bubble} size={size} /></div>;
}

// Login / Sign up: waves hello, covers eyes while typing a password, sad on error
export function AuthBuddy({ hello = "Welcome back! 👋", error = "", className = "" }) {
    const [mood, setMood] = useState("wave");
    const [bubble, setBubble] = useState(hello);
    const t = useRef();
    const set = (m, b, back) => {
        clearTimeout(t.current); setMood(m); setBubble(b);
        if (back) t.current = setTimeout(() => { setMood("idle"); setBubble(null); }, back);
    };
    useEffect(() => { set("wave", hello, 2800); return () => clearTimeout(t.current); }, [hello]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (error) set("sad", "Oops! Check your details 😕", 3200); }, [error]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const onIn = (e) => {
            const el = e.target;
            if (!(el instanceof HTMLInputElement)) return;
            if (el.type === "password") set("shy", "I'm not looking 🙈");
            else if (el.type === "email" || el.type === "text") set("idle", "Nice to see you 😊");
        };
        const onOut = (e) => { if (e.target instanceof HTMLInputElement) set("idle", null); };
        document.addEventListener("focusin", onIn);
        document.addEventListener("focusout", onOut);
        return () => { document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return <Buddy mood={mood} bubble={bubble} bubbleSide="left" className={className} size={170} />;
}

// Global pop-up: mount once (App.js). Any page can trigger it with the "g2c:buddy" event.
export function BuddyToast() {
    const [toast, setToast] = useState(null);
    const t = useRef();
    useWindowEvent("g2c:buddy", (e) => {
        const d = e.detail || {};
        clearTimeout(t.current);
        setToast({ mood: d.mood || "thumb", text: d.text || "", key: Date.now() });
        t.current = setTimeout(() => setToast(null), d.ms || 4200);
    });
    useEffect(() => () => clearTimeout(t.current), []);
    if (!toast) return null;
    return (
        <div key={toast.key} className="fixed z-[60] right-4 bottom-24 sm:right-6 flex items-end gap-2 pointer-events-none" style={{ animation: reduced() ? "none" : `g2cToastIn .5s ${SPRING} both` }}>
            <div className="mb-16 bg-white border border-slate-100 shadow-floating rounded-2xl rounded-br-sm px-4 py-3 text-sm font-semibold text-slate-800 max-w-[220px]">{toast.text}</div>
            <Buddy mood={toast.mood} size={90} look="left" />
            <style>{"@keyframes g2cToastIn{from{transform:translateY(40px) scale(.8);opacity:0}to{transform:none;opacity:1}}"}</style>
        </div>
    );
}

// Home page chat button: he stands behind it holding it. Mount next to the chat button.
// Button is 56px, fixed bottom-24 (96px) right-6 (24px) -> his chest sits right behind it.
export function ChatHolderBuddy({ open = false }) {
    const [s, play] = useBuddy("hold");
    const said = useRef(false);
    useEffect(() => {
        const t = setTimeout(() => { if (!said.current) { said.current = true; play([{ mood: "holdHappy", bubble: "Need help? 💬", ms: 3500 }]); } }, 6000);
        return () => clearTimeout(t);
    }, [play]);
    return (
        <div className="fixed z-[39] pointer-events-none" style={{
            right: 2, bottom: 50, transition: `transform .45s ${SPRING}, opacity .3s`,
            transform: open ? "translateY(60px)" : "none", opacity: open ? 0 : 1,
        }}>
            <Buddy mood={open ? "holdHappy" : s.mood} bubble={open ? null : s.bubble} size={100} look="left" />
        </div>
    );
}

// User dashboard welcome banner: waves, then nudges the user to the next step.
export function DashBuddy({ ready = false, name = "", emailVerified = true, kyc = "approved", pending = 0, completed = 0, total = 0, earned = 0, className = "" }) {
    const [s, play] = useBuddy("idle");
    const first = (name || "").trim().split(/\s+/)[0];
    const status = () => {
        if (!emailVerified) return { mood: "point", bubble: "Verify your email first ✉️" };
        if (kyc !== "approved") return { mood: "point", bubble: kyc === "pending" ? "Your ID is being checked ⏳" : "Let's verify your ID 🪪" };
        if (pending > 0) return { mood: "thumb", bubble: `We're on your ${pending} card${pending === 1 ? "" : "s"} ⏳` };
        if (total === 0) return { mood: "point", bubble: "Sell your first card! 👈" };
        if (Number(earned) > 0) return { mood: "celebrate", bubble: `You've earned $${earned}! 🎉` };
        return { mood: "thumb", bubble: "Looking good! 👍" };
    };
    const done = useRef(false);
    useEffect(() => {
        if (!ready || done.current) return;
        done.current = true;
        play([{ mood: "wave", bubble: `Hi ${first || "there"}! 👋`, ms: 2400 }, { ...status(), ms: 3800 }]);
    }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <div className={className} onMouseEnter={() => play([{ ...status(), ms: 3000 }])}>
            <Buddy mood={s.mood} bubble={s.bubble} size={120} flip look="right" bubbleSide="left" />
        </div>
    );
}

// Footer: keepy-uppies with a football. Click -> GOAL!
export function FootballBuddy({ className = "", size = 110 }) {
    const [s, play] = useBuddy("football");
    const ref = useSeenOnce(() => setTimeout(() => play([{ mood: "football", bubble: "Fancy a game? ⚽", ms: 3000 }]), 1200));
    const goal = () => play([{ mood: "goal", bubble: "GOAL! ⚽🎉", ms: 2600 }]);
    return (
        <div ref={ref} className={`cursor-pointer w-fit ${className}`} onClick={goal} title="Kick!">
            <Buddy mood={s.mood} bubble={s.bubble} bubbleSide="right" size={size} look="down" pants="#475569" />
        </div>
    );
}

export const buddySay = (mood, text, ms) => {
    try { window.dispatchEvent(new CustomEvent("g2c:buddy", { detail: { mood, text, ms } })); } catch (_) { /* old browsers */ }
};

export default Buddy;
